import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Auto-logout hook for session management
 * - Logs out user after 15 minutes of inactivity
 * - Logs out user after 2 hours of total session time
 * - Tracks mouse/keyboard activity
 */
const useAutoLogout = () => {
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const sessionTimerRef = useRef(null);
  
  // Configuration (in milliseconds)
  const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
  const SESSION_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
  const WARNING_TIME = 2 * 60 * 1000; // 2 minutes before logout

  const handleLogout = useCallback(() => {
    console.log('Auto-logout triggered');
    
    // Clear timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    
    // Show warning message
    alert('Your session has expired due to inactivity. Please login again.');
    
    // Logout and redirect
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const showWarning = useCallback(() => {
    const shouldContinue = window.confirm(
      'Your session will expire in 2 minutes due to inactivity. Click OK to continue your session.'
    );
    
    if (shouldContinue) {
      // Reset inactivity timer
      resetInactivityTimer();
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set warning timer (13 minutes - 2 minutes before logout)
    const warningTimer = setTimeout(showWarning, INACTIVITY_TIMEOUT - WARNING_TIME);
    
    // Set logout timer (15 minutes)
    inactivityTimerRef.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
    
    // Store warning timer for cleanup
    inactivityTimerRef.warningTimer = warningTimer;
  }, [handleLogout, showWarning, INACTIVITY_TIMEOUT, WARNING_TIME]);

  const checkSessionExpiry = useCallback(() => {
    const sessionStart = localStorage.getItem('sessionStart');
    if (!sessionStart) return;

    const sessionStartTime = new Date(sessionStart).getTime();
    const currentTime = new Date().getTime();
    const sessionDuration = currentTime - sessionStartTime;

    if (sessionDuration >= SESSION_TIMEOUT) {
      alert('Your session has expired (2 hours maximum). Please login again.');
      handleLogout();
    }
  }, [handleLogout, SESSION_TIMEOUT]);

  useEffect(() => {
    // Only set up auto-logout if user is authenticated
    if (!isAuthenticated()) return;

    // Check session expiry on mount
    checkSessionExpiry();

    // Set up session timeout timer
    const sessionStart = localStorage.getItem('sessionStart');
    if (sessionStart) {
      const sessionStartTime = new Date(sessionStart).getTime();
      const currentTime = new Date().getTime();
      const remainingTime = SESSION_TIMEOUT - (currentTime - sessionStartTime);
      
      if (remainingTime > 0) {
        sessionTimerRef.current = setTimeout(handleLogout, remainingTime);
      }
    }

    // Initialize inactivity timer
    resetInactivityTimer();

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Reset timer on any activity
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (inactivityTimerRef.warningTimer) {
        clearTimeout(inactivityTimerRef.warningTimer);
      }
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
      }
      
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, resetInactivityTimer, handleLogout, checkSessionExpiry, SESSION_TIMEOUT]);

  return null;
};

export default useAutoLogout;
